import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, TextField, FormLabel, IconButton, Grid } from "@material-ui/core";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import DmsApiService from "Services/DMS/dms-api.service";
import AlertToastr from "utils/alert";
import useTranslation from "Hooks/useTranslation";

const PlusIcon = ({ style }) => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 16 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={style}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M12.6667 2H3.33333C2.59333 2 2 2.6 2 3.33333V12.6667C2 13.4 2.59333 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V3.33333C14 2.6 13.4 2 12.6667 2ZM12.6667 12.6667H3.33333V3.33333H12.6667V12.6667ZM8.66667 11.3333H7.33333V8.66667H4.66667V7.33333H7.33333V4.66667H8.66667V7.33333H11.3333V8.66667H8.66667V11.3333Z" 
      fill="currentColor"
    />
  </svg>
);

const SendMessageToSnsModal = ({
  isOpen,
  isOpenChangeHandler,
  integrationData,
  fetchDevicesMessages,
}) => {
  const translation = useTranslation();

  const [message, setMessage] = useState({
    licenseId: "",
    channelNumber: "",
    sshEndpoints: [
      { ip: "localhost", port: "7002" },
      { ip: "localhost", port: "22" },
      { ip: "localhost", port: "443" }
    ],
  });

  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (integrationData) {
      setMessage({
        licenseId: integrationData.licenseId || "",
        channelNumber: integrationData.channelNumber || "",
        sshEndpoints: integrationData.sshEndpoints || [
          { ip: "localhost", port: "7002" },
          { ip: "localhost", port: "22" },
          { ip: "localhost", port: "443" }
        ],
      });
    }
  }, [integrationData]);

  useEffect(() => {
    if (integrationData) {
      setIsEdit(true);
    }
  }, [integrationData]);

  const setDefaultValues = () => {
    setMessage({
      licenseId: "",
      channelNumber: "",
      sshEndpoints: [
        { ip: "localhost", port: "7002" },
        { ip: "localhost", port: "22" },
        { ip: "localhost", port: "443" }
      ],
    });
  };

  const onCreateOrUpdate = async () => {
    const messageInfo = {
      licenseId: message.licenseId,
      channelNumber: message.channelNumber,
      sshEndpoints: message.sshEndpoints.filter(endpoint => endpoint.ip && endpoint.port),
    };

    if (isEdit && integrationData && integrationData.id) {
      messageInfo.id = integrationData.id;
    } 

    try {
      let response;
      if (isEdit) {
        response = await DmsApiService.editDeviceMessage(messageInfo);
        if (response.data.isSuccessful) {
          AlertToastr.showAlert(translation.notificationsDeviceMessageUpdated);
        }
      } else {
        response = await DmsApiService.sendMessageToSns(messageInfo);
        if (response.data.isSuccessful) {
          AlertToastr.showAlert(translation.notificationsDeviceMessageCreated);
        }
      }
      onClose();
      if (fetchDevicesMessages) {
        fetchDevicesMessages();
      }
    } catch (error) {
      if (error && error.response && error.response.data) {
        const errData = error.response.data;
        if (Array.isArray(errData.errors) && errData.errors.length > 0) {
          errData.errors.forEach((e) => {
            AlertToastr.showErrorAlert(e.ErrorMessage);
          });
        } else if (typeof errData.Message === "string") {
          AlertToastr.showErrorAlert(errData.Message);
        } else if (typeof errData.message === "string") {
          AlertToastr.showErrorAlert(errData.message);
        } else {
          AlertToastr.showErrorAlert("An unexpected error occurred.");
        }
      } else {
        AlertToastr.showErrorAlert("An unexpected error occurred.");
      }
    }
  };

  const onClose = () => {
    setDefaultValues();
    setIsEdit(false);
    isOpenChangeHandler(false);
  };

  const handleSubmit = async () => {
    onCreateOrUpdate();
  };

  const addSshEndpoint = () => {
    setMessage({
      ...message,
      sshEndpoints: [...message.sshEndpoints, { ip: "", port: "" }],
    });
  };

  const removeSshEndpoint = (index) => {
    if (message.sshEndpoints.length > 1) {
      const newEndpoints = message.sshEndpoints.filter((_, i) => i !== index);
      setMessage({
        ...message,
        sshEndpoints: newEndpoints,
      });
    }
  };

  const updateSshEndpoint = (index, field, value) => {
    const newEndpoints = [...message.sshEndpoints];
    newEndpoints[index] = { ...newEndpoints[index], [field]: value };
    setMessage({
      ...message,
      sshEndpoints: newEndpoints,
    });
  };

  const isFormValid = useMemo(() => {
    return message.licenseId !== "" && message.sshEndpoints.some(endpoint => endpoint.ip && endpoint.port);
  }, [message]);

  return (
    <Dialog
      open={isOpen}
      className="respModal scrollContentModal calendarModal autoHeight datepicker-modal"
    >
      <form action="">
        <div className="formContentHolder">
          <div className="modalTitleHolder">
            {!isEdit ? (
              <h2>{translation.sendMessageToSnsModalCreateMessageLabel}</h2>
            ) : (
              <h2>{translation.sendMessageToSnsModalEditMessageLabel}</h2>
            )}
          </div>

          <div className="formContent">
            <div className="formContentBlock">
              <div>
                <FormLabel
                  component="legend"
                  style={{ lineHeight: "20px"}}
                >
                  {translation.sendMessageToSnsModalLicenseLabel}
                </FormLabel>
              </div>
              <div>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="licenseId"
                  label="License ID"
                  name="licenseId"
                  inputProps={{
                    autoComplete: "off",
                  }}
                  value={message.licenseId}
                  onChange={(e) =>
                    setMessage({
                      ...message,
                      licenseId: e.target.value,
                    })
                  }
                />
              </div>
              

              
              <div style={{ marginTop: "8px" }}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  id="channelNumber"
                  label="Channel Number"
                  name="channelNumber"
                  inputProps={{
                    autoComplete: "off",
                  }}
                  value={message.channelNumber}
                  onChange={(e) =>
                    setMessage({
                      ...message,
                      channelNumber: e.target.value,
                    })
                  }
                />
              </div>
              
              <div style={{ marginTop: "16px" }}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px"}}>
                  <FormLabel
                    component="legend"
                    style={{ lineHeight: "24px", margin: 0 }}
                  >
                    {translation.sendMessageToSnsModalSshEndpointsLabel}
                  </FormLabel>
                  <Button
                    startIcon={<PlusIcon style={{color: "rgba(0, 0, 0, 0.54)"}} />}
                    onClick={addSshEndpoint}
                    variant="text"
                    size="small"
                    id="sendMessageToSnsModalAddSshEndpointBtn"
                  >
                    <span className="сheckboxLabel">
                      {translation.sendMessageToSnsModalAddSshEndpointLabel}
                    </span>
                  </Button>
                </div>
              </div>
              
              {message.sshEndpoints.map((endpoint, index) => (
                <div key={index} style={{ marginTop: "8px" }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        id={`ssh-ip-${index}`}
                        label={translation.sendMessageToSnsModalIpAddressLabel}
                        name="ip"
                        inputProps={{
                          autoComplete: "off",
                        }}
                        value={endpoint.ip}
                        onChange={(e) =>
                          updateSshEndpoint(index, "ip", e.target.value)
                        }
                        placeholder="192.168.1.1"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        id={`ssh-port-${index}`}
                        label={translation.sendMessageToSnsModalPortLabel}
                        name="port"
                        inputProps={{
                          autoComplete: "off",
                        }}
                        value={endpoint.port}
                        onChange={(e) =>
                          updateSshEndpoint(index, "port", e.target.value)
                        }
                        placeholder="22"
                      />
                    </Grid>
                    <Grid item xs={1} style={{ display: "flex", justifyContent: "flex-end" }}>
                      {message.sshEndpoints.length > 1 && (
                        <IconButton
                          onClick={() => removeSshEndpoint(index)}
                          className="iconBtnSm"
                          title={translation.sendMessageToSnsModalRemoveSshEndpointLabel}
                        >
                          <HighlightOffIcon style={{color: "rgba(0, 0, 0, 0.54)"}} />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </div>
              ))}
            </div>
          </div>
          <div className="btnHolder  fixedWidthBtn">
            <Button onClick={() => onClose(null)} variant="outlined">
              {translation.cancelButtonLabel}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              id="sendMessageToSnsModalSendBtn"
            >
              {translation.sendMessageToSnsModalSendButtonLabel}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default SendMessageToSnsModal;
